import type { IconName } from '@ovpn/ui/icon';

import { getEnvVar } from '../utils';

import { routes } from './routes';

export type SidebarSublink = {
    /**
     * Menu title.
     */
    name: string;

    /**
     * Menu router link or href.
     */
    to: string;

    gtmId: string;

    /**
     * anchor target attribute
     */
    target?: string;

    /**
     * If menu should be decodated with a badge.
     */
    decorator?: 'new' | 'beta';

    /**
     * When link should be marked as active.
     * Either regex rule or a list of string locations.
     */
    activeOn?: RegExp | string[];
};

export type SidebarLink = {
    name: string;
    to: string;
    icon: IconName;
    gtmId: string;

    children?: Record<string, SidebarSublink>;

    /**
     * If menu should have a divider above it
     */
    divided?: boolean;

    /**
     * When link should be marked as active.
     * Either regex rule or a list of string locations.
     */
    activeOn?: RegExp | string[];
};

function activateOnExcept(on: string, except: string[]) {
    const negativeLookahead = `(?!${except.join('|')})`;
    return new RegExp(`${negativeLookahead}(${on}/.*)`);
}

export const menuLinks = {
    status: {
        name: 'Status',
        to: routes.statusPage,
        icon: 'chart-line',
        gtmId: 'status',
    },
    users: {
        name: 'Users',
        to: routes.users,
        icon: 'users',
        gtmId: 'users',
        children: {
            users: {
                name: 'Users',
                to: routes.users,
                gtmId: 'users',
                activeOn: [routes.userDetails, routes.userDetailsDevices],
            },
            groups: {
                name: 'Groups',
                to: routes.groups,
                gtmId: 'groups',
                activeOn: [
                    routes.groupDetails,
                    routes.groupDetailsUsers,
                    routes.groupDetailsAccess,
                ],
            },
            tenantDevices: {
                name: 'Devices',
                to: routes.devices,
                gtmId: 'devices',
            },
            locationContext: {
                name: 'Location Context',
                to: routes.locationContext.all,
                activeOn: Object.values(routes.locationContext),
                gtmId: 'location_context',
            },
            devicePosture: {
                name: 'Device Posture',
                to: routes.devicePosture.all,
                decorator: 'beta',
                activeOn: Object.values(routes.devicePosture),
                gtmId: 'device_posture',
            },
        },
    },
    network: {
        name: 'Networks',
        to: routes.networks,
        icon: 'world',
        gtmId: 'networks',
        children: {
            networks: {
                name: 'Networks',
                to: routes.networks,
                gtmId: '',
                activeOn: activateOnExcept(routes.networks, [
                    routes.networkApplications,
                    routes.networkConnectors,
                    routes.networkIPServices,
                ]),
            },
            applications: {
                name: 'Applications',
                to: routes.networkApplications,
                gtmId: 'applications_networks',
            },
            ipServices: {
                name: 'IP Services',
                to: routes.networkIPServices,
                gtmId: 'ip_services_networks',
            },
            connectors: {
                name: 'Connectors',
                to: routes.networkConnectors,
                gtmId: 'connectors_networks',
            },
        },
    },
    hosts: {
        name: 'Hosts',
        to: routes.hosts,
        icon: 'server',
        gtmId: 'hosts',
        children: {
            hosts: {
                name: 'Hosts',
                to: routes.hosts,
                gtmId: 'hosts',
                activeOn: activateOnExcept(routes.hosts, [
                    routes.hostApplications,
                    routes.hostConnectors,
                    routes.hostIPServices,
                ]),
            },
            applications: {
                name: 'Applications',
                to: routes.hostApplications,
                gtmId: 'applications_hosts',
            },
            ipServices: {
                name: 'IP Services',
                to: routes.hostIPServices,
                gtmId: 'ip_services_hosts',
            },
            connectors: {
                name: 'Connectors',
                to: routes.hostConnectors,
                gtmId: 'connectors_hosts',
            },
        },
    },
    access: {
        name: 'Access',
        to: routes.accessControl,
        icon: 'category',
        gtmId: 'access',

        children: {
            visibility: {
                name: 'Visibility',
                to: routes.accessVisibility,
                gtmId: 'visibility_access',
                activeOn: new RegExp(`${routes.accessVisibility}/.*`),
            },
            groups: {
                name: 'Groups',
                to: routes.accessControl,
                gtmId: 'groups_access',
                activeOn: [
                    routes.accessGroupCreate,
                    routes.accessGroupEdit,
                    routes.accessGroupEditV2,
                ],
            },
            internet: {
                name: 'Internet',
                to: routes.accessInternet.groups,
                gtmId: 'internet_access',
                activeOn: [routes.accessInternet.networks, routes.accessInternet.hosts],
            },
        },
    },
    shieldV2: {
        name: 'Shield',
        to: routes.cyberShieldV2.overview,
        icon: 'shield',
        gtmId: 'shield',
        children: {
            overview: {
                name: 'Overview',
                to: routes.cyberShieldV2.overview,
                gtmId: 'overview_shield',
            },
            configuration: {
                name: 'Configuration',
                to: routes.cyberShieldV2.configuration,
                gtmId: 'configuration_shield',

                activeOn: new RegExp(`${routes.cyberShieldV2.configuration}/.*`),
            },
            metrics: {
                name: 'Metrics',
                to: routes.cyberShieldV2.metrics,
                gtmId: 'metrics_shield',
            },
        },
    },
    publishing: {
        name: 'AppHub',
        to: routes.publishing,
        gtmId: 'apphub',
        activeOn: new RegExp(`${routes.publishing}/.*`),
        icon: 'apps',
    },
    apiAndLogs: {
        name: 'API & Logs',
        to: routes.ssoApi,
        icon: 'api-app',
        gtmId: 'api_and_logs',
        children: {
            cloudApi: {
                name: 'API',
                to: routes.ssoApi,
                gtmId: '',
                activeOn: new RegExp(`${routes.ssoApi}/.*`),
            },
            auditLogs: {
                name: 'Audit Log',
                to: routes.auditLog,
                gtmId: '',
                activeOn: new RegExp(`${routes.auditLog}/.*`),
                decorator: 'beta',
            },
            dnsResolutions: {
                name: 'DNS Log',
                to: routes.dnsResolutions,
                gtmId: '',
                activeOn: new RegExp(`${routes.dnsResolutions}/.*`),
                decorator: 'beta',
            },
            logStreaming: {
                name: 'Log Streaming',
                to: routes.logStreaming,
                gtmId: '',
                activeOn: new RegExp(`${routes.logStreaming}/.*`),
            },
        },
    },
    settings: {
        name: 'Settings',
        to: routes.networkSettings,
        icon: 'settings',
        gtmId: 'settings',
        children: {
            vpn: {
                name: 'WPC',
                to: routes.networkSettings,
                gtmId: 'wpc_settings',
            },
            users: {
                name: 'Users',
                to: routes.userSettings,
                gtmId: 'users_settings',
            },
            userAuth: {
                name: 'User Authentication',
                to: routes.userAuth,
                gtmId: 'user_authentication_settings',
                activeOn: [
                    routes.samlGroupMapping,
                    ...Object.values(routes.ldapSettings),
                    ...Object.values(routes.ssoSamlConfigure),
                ],
            },
            dns: {
                name: 'DNS',
                to: routes.dnsServers,
                gtmId: 'dns_settings',
                activeOn: [routes.dnsRecords],
            },
            notifications: {
                name: 'Notifications',
                to: routes.notificationPreferences,
                gtmId: 'notifications_settings',
            },
        },
    },
    documentation: {
        name: 'Help',
        to: routes.quickStart,
        icon: 'news',
        gtmId: 'help',
        divided: true,
        children: {
            wizard: {
                name: 'Restart onboarding',
                to: routes.wizard.restart,
                gtmId: 'restart_onboarding',
            },
            quickStart: {
                name: 'Quick Start',
                to: routes.quickStart,
                gtmId: 'quick_start',
            },
            knowledgeBase: {
                name: 'Knowledge Base',
                to: getEnvVar('DOCUMENTATION_LINK'),
                target: '_blank',
                gtmId: 'knowledge_base',
            },
            support: {
                name: 'Support',
                to: 'mailto:cloud-support@openvpn.net',
                target: '_self',
                gtmId: 'support',
            },
        },
    },
    getConnected: {
        name: 'Download App',
        to: routes.getConnectedUnified,
        icon: 'download',
        gtmId: 'download_app',
        divided: true,
    },
    instructions: {
        name: 'Get Connected',
        to: routes.instructions,
        icon: 'help',
        gtmId: 'get_connected',
    },
    devices: {
        name: 'My Devices',
        to: routes.devices,
        icon: 'devices',
        gtmId: 'my_devices',
    },
} satisfies Record<string, SidebarLink>;
